import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Code, Server, Cloud, Database, Cpu, GitBranch } from 'lucide-react';
import { skills } from '../data/mock';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const SkillsSection = () => {
  const [activeCategory, setActiveCategory] = useState('frontend');

  const categories = [
    { id: 'frontend', label: 'Frontend', icon: Code, color: 'text-blue-500' },
    { id: 'backend', label: 'Backend', icon: Server, color: 'text-emerald-500' },
    { id: 'microsoft', label: 'Microsoft', icon: Cpu, color: 'text-purple-500' },
    { id: 'ai', label: 'AI/ML', icon: GitBranch, color: 'text-orange-500' },
    { id: 'databases', label: 'Databases', icon: Database, color: 'text-cyan-500' },
    { id: 'devops', label: 'DevOps', icon: GitBranch, color: 'text-pink-500' },
    { id: 'cloud', label: 'Cloud', icon: Cloud, color: 'text-indigo-500' }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <section id="skills" className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
        >
          {/* Section Header */}
          <div className="text-center mb-16">
            <motion.div variants={itemVariants} className="inline-block">
              <Badge variant="outline" className="mb-4">
                <Cpu className="w-3 h-3 mr-1" />
                Technical Expertise
              </Badge>
            </motion.div>
            <motion.h2
              variants={itemVariants}
              className="text-3xl sm:text-4xl font-bold mb-4"
            >
              Skills & Technologies
            </motion.h2>
            <motion.p
              variants={itemVariants}
              className="text-lg text-muted-foreground max-w-2xl mx-auto"
            >
              Comprehensive expertise across modern development stacks
            </motion.p>
          </div>

          {/* Skills Tabs */}
          <motion.div variants={itemVariants}>
            <Tabs value={activeCategory} onValueChange={setActiveCategory} className="w-full">
              <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 mb-8 h-auto gap-2">
                {categories.map((category) => {
                  const Icon = category.icon;
                  return (
                    <TabsTrigger
                      key={category.id}
                      value={category.id}
                      className="flex items-center space-x-2 py-3"
                    >
                      <Icon className={`w-4 h-4 ${category.color}`} />
                      <span className="hidden sm:inline">{category.label}</span>
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {categories.map((category) => (
                <TabsContent key={category.id} value={category.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
                  >
                    {skills[category.id]?.map((skill, index) => (
                      <motion.div
                        key={skill.name}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Card className="hover:shadow-lg transition-all hover:-translate-y-1">
                          <CardContent className="p-6">
                            <div className="flex items-center justify-between mb-3">
                              <h3 className="font-semibold text-sm">{skill.name}</h3>
                              <span className="text-xs font-medium text-muted-foreground">
                                {skill.level}%
                              </span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${skill.level}%` }}
                                transition={{ duration: 0.8, delay: index * 0.05 }}
                                className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full"
                              />
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </motion.div>
                </TabsContent>
              ))}
            </Tabs>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default SkillsSection;
